import React, { useRef, useState } from "react";

import { Button } from "antd";
import { motion } from "framer-motion/dist/framer-motion";
import moment from "moment";
import Highlighter from "react-highlight-words";
import { toast } from "react-toastify";

import StyledTooltip from "../../Common/UIComponents/StyledTooltip";
import { useAccessStatus } from "../../grace-period/hooks/useAccessStatus";
import EnvConfirmModal from "../Analytics/Components/EnvConfirmModal";
// Temporarily disabled: CAPA feature (CSS clashes when statically imported)
// import EditSampleTestModal from "../CAPA/components/EditSampleTestModal";
// import RescheduleModal from "../CAPA/components/RescheduleModal";
import PopoverMenu from "../components/PopoverMenu";
import EVENT_SPEC_STYLE_MAP from "../constants";

import { isResultRevealingSpec } from "../../grace-period/utils";

import { marksampled } from "../../../actions/envCalendar";
import { envEditCorrectiveSample } from "../../../actions/envCorrectiveAction";

import { ReactComponent as ContextMenuIcon } from "../../../assets/images/environment/ContextMenuIcon.svg";
// Temporarily disabled: CAPA-related calendar actions (Edit/Reschedule use CAPA CSS modules)
// import { ReactComponent as EditPencilIcon } from "../../../assets/images/environment/EditPencilIcon.svg";
// import { ReactComponent as RescheduleIcon } from "../../../assets/images/environment/RescheduleIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/images/environment/TrashIcon.svg";

/**
 * Checks if marking as sampled allowed
 * @param {Date} event_date event date
 * @param {"today" | "add" | "subtract"} type today or add/subtract days
 * @param {number} days no. of days - optional if type = "today"
 * @returns {boolean}
 */
export const isMarkingAllowed = (event_date, type = "today", days = 1) => {
  // Can be marked if submission date is between specified future days
  if (type === "add") {
    return moment(event_date).isBetween(
      moment().startOf("day"),
      moment().clone().add(days, "day").endOf("day"),
      "day",
      "[]",
    );
  }
  // Can be marked if submission date is between specified past days
  if (type === "subtract") {
    return moment(event_date).isBetween(
      moment().clone().subtract(days, "day").startOf("day"),
      moment().endOf("day"),
      "day",
      "[]",
    );
  }
  // default is to check only today's date
  return moment(event_date).isBetween(
    moment().startOf("day"),
    moment().endOf("day"),
    "day",
    "[]",
  ); // Can be marked if submission date is today only
};

export const getSpecMapKey = (specsFlag, submissionStatus, sampled) => {
  let specMapKey = specsFlag;
  if (parseInt(submissionStatus, 10) >= 0) { // if Testing in Process or Testing Complete
    specMapKey = specsFlag;
  } else if (submissionStatus === "-2" && sampled) { // If submission is draft and it is sampled, then always used the "-1" (orange) color
    specMapKey = -1;
  } else if (submissionStatus === "-1" && !sampled) { // If submitted, the not sampled, still show "-2"
    specMapKey = -2;
  }
  return specMapKey;
};

export default function LocationDetails({
  sampleId,
  specsFlag,
  submissionId,
  sampleType,
  sampled,
  afterMarkingSample,
  draggableId,
  setCardLoading,
  event_date,
  submissionStatus,
  submissionCategory,
  testList,
  currentEvent,
  // eventsByDate,
  // markedDates,
}) {
  const { areResultsLocked } = useAccessStatus();
  const [confirmMarkAsScheduled, setConfirmMarkAsScheduled] = useState(false);
  const [confirmUnmarkAsScheduled, setConfirmUnmarkAsScheduled] = useState(false);
  const [openPopover, setOpenPopover] = useState(false);
  // Temporarily disabled: CAPA feature (Edit/Reschedule modals pull CAPA CSS)
  // const [editSampleTest, setEditSampleTest] = useState(false); // open edit modal for samples; corrective action feature; Default false
  // const [rescheduleTest, setRescheduleTest] = useState(false); // open reschedule modal for samples; corrective action feature; Default false
  const [isDeletingConfirm, setIsDeletingConfirm] = useState(false); // open delete modal for samples; corrective action feature; Default false
  // eslint-disable-next-line no-unused-vars
  const [deleteLoading, setDeleteLoading] = useState(false);
  // const [showLocationContextMenu, setShowLocationContextMenu] = useState(false);
  const ref = useRef();

  // Temporarily disabled: only used by Edit/Reschedule CAPA menu items
  // const isSwabathon = submissionCategory === "swabathon";
  // const isCorrectiveAction = submissionCategory === "corrective_action";
  // const isEditable = !isSwabathon && !isCorrectiveAction;

  const popoverMenuItems = [
    // Temporarily disabled: CAPA feature (Edit/Reschedule modals pull CAPA CSS)
    // {
    //   title: "Edit Tests",
    //   icon: <EditPencilIcon />,
    //   isVisible: isEditable,
    //   onClick: () => {
    //     setOpenPopover(false);
    //     setEditSampleTest(true);
    //   },
    // },
    // {
    //   title: "Reschedule",
    //   icon: <RescheduleIcon />,
    //   isVisible: isEditable,
    //   onClick: () => {
    //     setOpenPopover(false);
    //     setRescheduleTest(true);
    //   },
    // },
    {
      title: "Delete",
      icon: <TrashIcon />,
      isVisible: () => (
        submissionCategory !== "swabathon" || specsFlag === "-2"
      ) && submissionStatus === "-2",
      onClick: () => {
        setOpenPopover(false);
        setIsDeletingConfirm(true);
      },
    },
  ];

  /**
   * Handles marking samples
   * @param {String} action
   */
  const markSample = async (action) => {
    setCardLoading({ isLoading: true, draggableId });
    const response = await marksampled(submissionId, sampleId, sampleType, action);
    if (response.success) {
      toast.success(`Sample ${action.toLowerCase()}ed`);
    } else {
      toast.success(response.message);
    }
    setConfirmMarkAsScheduled(false);
    setConfirmUnmarkAsScheduled(false);
    await afterMarkingSample(); // Refetch data
    setCardLoading({ isLoading: false, draggableId: "" });
  };

  /**
   * Delete location
   */
  const handleDeleteLocation = async () => {
    setDeleteLoading(true);
    const response = await envEditCorrectiveSample("delete", submissionId, sampleId);
    if (response.success) {
      setDeleteLoading(false);
      setIsDeletingConfirm(false);
      toast.success("Sample deleted successfully");
      await afterMarkingSample(); // refresh data
    } else {
      setDeleteLoading(false);
      setIsDeletingConfirm(false);
      toast.error(response.message);
    }
  };

  // const handleMouseEnter = (e) => {
  //   e.stopPropagation();
  //   if (e.currentTarget !== ref?.current) return;
  //   setShowLocationContextMenu(true);
  // };

  // const handleMouseleave = (e) => {
  //   e.stopPropagation();
  //   if (e.currentTarget !== ref?.current) return;
  //   setShowLocationContextMenu(false);
  // };

  /**
   * Show confirmation when status has to update
   * @param {Boolean} isMarkAsScheduled
   */
  const statusUpdateConfirm = (isMarkAsScheduled = true) => {
    const text = isMarkAsScheduled ? "Do you want to mark this location as sampled?" : "Unmark sample?";
    const confirmAction = isMarkAsScheduled ? () => markSample("Mark") : () => markSample("Unmark");
    const cancelAction = isMarkAsScheduled ? () => setConfirmMarkAsScheduled(false) : () => setConfirmUnmarkAsScheduled(false);
    return (
      <motion.li
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`Event_LocationDetails Event_LocationConfirmUpdateStatus ${submissionCategory === "randomizer" ? "Event_LocationDetails--RandomizerMotion" : ""}`}
        style={submissionCategory === "randomizer" ? undefined : {
          background: isMarkAsScheduled ? "var(--color-content-warning-bg)" : "var(--color-bg-primary)",
        }}
      >
        <p>{text}</p>
        <div className="Event_LocationConfirmUpdateStatus__Buttons">
          <Button
            type="default"
            className="Event_LocationConfirmUpdateStatus__Button"
            onClick={confirmAction}
          >
            Yes
          </Button>
          <Button
            type="default"
            className="Event_LocationConfirmUpdateStatus__Button"
            onClick={cancelAction}
          >
            No
          </Button>
        </div>
      </motion.li>
    );
  };

  /**
   * Renders button to mark/unmark
   * @param {Boolean} isMarkAsScheduled
   */
  const updateSampledStatusButton = (isMarkAsScheduled = true) => (
    <Button
      htmlType="button"
      className={isMarkAsScheduled ? "EventCard__MarkAsSampledButton" : "EventCard__UnMarkAsSampledButton"}
      onClick={() => {
        if (!testList?.length) {
          toast.error("No tests here. Please add test(s).");
          return;
        }
        if (isMarkAsScheduled) {
          setConfirmMarkAsScheduled(true);
          return;
        }
        setConfirmUnmarkAsScheduled(true);
      }}
    >
      {isMarkAsScheduled ? "Mark as sampled" : "Unmark Sample"}
    </Button>
  );

  const renderDeleteSampleConfirmContent = () => {
    const isLastSample = currentEvent?.samples_list?.length === 1;
    const date = moment(event_date).format("MMM D, YYYY");
    const text = `Are you sure you want to delete ${sampleId} scheduled on ${date}?${isLastSample ? " This will also delete the submission." : ""}`;
    return (
      <p className="LocationDetails__DeleteConfirmation truncated-text">
        <Highlighter
          highlightClassName="LocationDetails__DeleteConfirmation--highlighted"
          searchWords={[date]}
          autoEscape
          textToHighlight={text}
        />
      </p>
    );
  };

  if (confirmMarkAsScheduled) return statusUpdateConfirm();

  if (confirmUnmarkAsScheduled) return statusUpdateConfirm(false);

  const specMapKey = getSpecMapKey(specsFlag, submissionStatus, sampled);
  const isSampleResultLocked = areResultsLocked && isResultRevealingSpec(specMapKey);
  const color = isSampleResultLocked ? EVENT_SPEC_STYLE_MAP["-2"] : EVENT_SPEC_STYLE_MAP[specMapKey];
  const showRandomizerGradientStyle = !(submissionStatus === "-1") && !sampled && submissionCategory === "randomizer"
    && (String(specMapKey) === "-2" || String(specMapKey) === "-1");

  const styles = showRandomizerGradientStyle
    ? { color: color.text_color }
    : {
      background: color.sample_bg_color,
      color: color.text_color,
    };

  const isUnmarked = !sampled;
  const isMarked = sampled && submissionStatus === "-2"; // Only allow mark sample in draft status and it's sampled
  const hideEditSamplesPopup = submissionStatus === "-2" || submissionStatus === "-1";
  const visibleSampleMenuItems = popoverMenuItems.filter((item) => {
    const isVisible = typeof item.isVisible === "function" ? item.isVisible() : item.isVisible;
    return isVisible !== false;
  });
  const showSampleContextMenu = hideEditSamplesPopup && visibleSampleMenuItems.length > 0;
  return (
    <>
      <StyledTooltip
        title={isSampleResultLocked ? null : EVENT_SPEC_STYLE_MAP[specMapKey]?.display_name}
        placement="topLeft"
      >

        {/* <div className="Event_LocationDetails_Randomizer--BorderWrapperInner"> */}
        <div
          ref={ref}
          className={`Event_LocationDetails ${showRandomizerGradientStyle ? "Event_LocationDetails--Randomizer" : ""}`}
          style={styles}
          // onMouseEnter={handleMouseEnter}
          // onMouseLeave={handleMouseleave}
        >
          <div className="display-flex">
            <span className="truncated-text">{sampleId}</span>
            {showSampleContextMenu && (
              <PopoverMenu menuItems={popoverMenuItems} open={openPopover} setOpen={setOpenPopover}>
                <button type="button" className="Popup-menu">
                  <ContextMenuIcon />
                </button>
              </PopoverMenu>
            )}
          </div>
          {(isMarkingAllowed(event_date) && isUnmarked && parseInt(submissionStatus) < 0) && updateSampledStatusButton(true)}
          {(isMarkingAllowed(event_date) && isMarked && parseInt(submissionStatus) < 0) && updateSampledStatusButton(false)}
          {/* </div> */}
        </div>
      </StyledTooltip>
      {/* Temporarily disabled: CAPA feature (CSS clashes when module is imported)
      {editSampleTest && (
        <EditSampleTestModal
          sampleId={sampleId}
          submissionId={submissionId}
          testList={testList}
          afterMarkingSample={afterMarkingSample}
          onCancel={() => setEditSampleTest(false)}
        />
      )}
      {rescheduleTest && (
        <RescheduleModal
          sampleId={sampleId}
          submissionId={submissionId}
          currentEvent={currentEvent}
          eventDate={event_date}
          eventsByDate={eventsByDate}
          markedDates={markedDates}
          afterMarkingSample={afterMarkingSample}
          onCancel={() => setRescheduleTest(false)}
        />
      )}
      */}
      {isDeletingConfirm && (
        <EnvConfirmModal
          width="400px"
          headerText="Delete"
          buttonText={["Cancel", "Delete"]}
          loading={deleteLoading}
          renderExtraContent={renderDeleteSampleConfirmContent()}
          buttonFunctions={[
            () => setIsDeletingConfirm(false),
            handleDeleteLocation,
          ]}
        />
      )}
    </>
  );
}
